import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { useSelector } from "react-redux";
import { WizardDialog, WizardStep } from "component/shared/wizard-dialog";
import { CodeBlock } from "component/shared/code-block";
import { FormField } from "component/shared/form-fields";
import { User } from "model/user";
import { Page } from "model/page";
import { RootState, useAppThunkDispatch } from "redux/store";
import { getIntegrationByTypeId, getPageByTypeId } from "redux/selector";
import { saveUser } from "redux/slice/authentication";
import { fetchPages, savePage } from "redux/slice/pages";
import { IntegrationType } from "type/integration-type";
import { PageType } from "type/page-type";
import { PlatformChoice } from "type/platform-choice";
import * as kestrelEmbedCode from "shared/kestrel-embed-code";
import { ContactSupportLink, CopyValue, PageSetupInstructions, SetupCallout, SetupText } from "../page-setup";
import { normalizeWebsiteUrl, pageUrl, WEBSITE_URL_ERROR } from "./website-url";
import { PlatformRadioGroup } from "../platform-radio-group";
import { unwrap } from "../unwrap";
import { WIX_APP_MARKET_URL } from "../constants";
import { styles } from "./style";

interface OwnProps {
	open: boolean;
	onClose: () => void;
	user: User;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

/**
 * The Set Up IDX wizard (MAX-4419 tasks 6 and 7; VPLAT 3, 12, 13, 16-21, 23, 24), opened from the IDX
 * page's Website block. It opens on the client's current platform: Wix points to the App Market
 * install (task 7; VPLAT 12, 13), WordPress just saves the platform (its key is on the page), and Other
 * walks through the platform and URL, head script, Default IDX Page, body script and Search page.
 * Every branch ends at Complete Setup, with no connection check (D1).
 */
const Component: React.FC<Props> = ({ classes = {}, open, onClose, user }) => {
	const dispatch = useAppThunkDispatch();
	const hasWixApp = useSelector((state: RootState) => !!getIntegrationByTypeId(state, IntegrationType.WIX_APP));
	const defaultPage = useSelector((state: RootState) => getPageByTypeId(state, PageType.DEFAULT.id));
	const searchPage = useSelector((state: RootState) => getPageByTypeId(state, PageType.LISTINGS.id));

	const startChoice = PlatformChoice.fromWebsitePlatform(user.websitePlatform, hasWixApp);
	const [choice, setChoice] = useState<PlatformChoice>(startChoice);
	const [urlInput, setUrlInput] = useState(user.baseUrl || "");
	const [urlError, setUrlError] = useState(false);

	// Each opening starts from the client's saved platform and URL.
	useEffect(() => {
		if (open) {
			setChoice(startChoice);
			setUrlInput(user.baseUrl || "");
			setUrlError(false);
			dispatch(fetchPages());
		}
	}, [open]); // eslint-disable-line

	const websitePlatform = choice.toWebsitePlatform(user.websitePlatform);
	const baseUrl = normalizeWebsiteUrl(urlInput) ?? user.baseUrl ?? "";

	const savePlatformAndUrl = async () => {
		const normalized = normalizeWebsiteUrl(urlInput);
		if (!choice.wix && !normalized) {
			setUrlError(true);
			return false;
		}
		setUrlInput(normalized ?? urlInput);
		// Wix hides the URL, so a value typed under another platform is never saved with it.
		const nextBaseUrl = choice.wix ? user.baseUrl : normalized ?? user.baseUrl;
		if (websitePlatform !== user.websitePlatform || nextBaseUrl !== user.baseUrl) {
			await unwrap(dispatch(saveUser({ user: { ...user, websitePlatform, baseUrl: nextBaseUrl } })));
			// ihf-root creates the IDX pages for a non-WordPress client when they are listed.
			await unwrap(dispatch(fetchPages()));
		}
	};

	// Complete Setup marks both pages the wizard walks through. The Default IDX Page's `enabled` is read
	// by nothing else (links route through it either way), so it's free to record that its step was
	// done; the IDX page hides its setup banner once both are on.
	const enableSetupPages = async () => {
		for (const setupPage of [defaultPage, searchPage]) {
			if (setupPage && !setupPage.enabled) {
				const page: Page = { ...setupPage, enabled: true };
				await unwrap(dispatch(savePage({ page })));
			}
		}
	};

	// VPLAT 13: under the Wix option, unless the Wix app is already installed.
	const wixNotice = (
		<div className={classes.wixNotice}>
			<Icons.Info size={16} className={classes.wixNoticeIcon} />
			<div className={classes.wixNoticeBody}>
				<SetupText>
					Wix sites use the iHomefinder app from the Wix App Market. Install it to get started.
				</SetupText>
				<Mui.Button
					variant="text"
					color="primary"
					className={classes.wixInstall}
					href={WIX_APP_MARKET_URL}
					target="_blank"
					rel="noopener noreferrer"
					endIcon={<Icons.ExternalLink size={16} />}
				>
					Install iHomefinder App for Wix
				</Mui.Button>
			</div>
		</div>
	);

	const platformStep: WizardStep = {
		key: "platform",
		counted: false,
		onContinue: savePlatformAndUrl,
		content: (
			<div className={classes.section}>
				<SetupText>To begin setting up IDX, select your platform and provide your website URL below:</SetupText>
				<Mui.FormControl component="fieldset">
					<Mui.FormLabel component="legend" id="connect-idx-platform-label" className={classes.label} focused={false}>
						Platform
					</Mui.FormLabel>
					<PlatformRadioGroup
						value={choice}
						onChange={setChoice}
						aria-labelledby="connect-idx-platform-label"
						after={(option) => (option.wix && choice.wix && !hasWixApp ? wixNotice : undefined)}
					/>
				</Mui.FormControl>
				{!choice.wix && <Mui.Divider />}
				{!choice.wix && (
					<FormField
						label="Website URL"
						required
						placeholder="https://www.yourdomain.com"
						value={urlInput}
						error={urlError}
						helperText={urlError ? WEBSITE_URL_ERROR : undefined}
						onChange={(event) => {
							setUrlInput(event.target.value);
							setUrlError(false);
						}}
					/>
				)}
			</div>
		),
	};

	const otherSteps: WizardStep[] = [
		{
			key: "head-script",
			title: "Add the Script to Your Website",
			content: (
				<>
					<SetupCallout>
						Copy and paste this script into the &lt;head&gt; section of your website. It lets IDX content, like listings or search, load on your pages.
					</SetupCallout>
					<SetupText>
						The &lt;head&gt; section of your site is typically found in your platform's site-wide 'custom code' or 'head scripts' settings. This script must be present on every page where IDX content will load.
					</SetupText>
					<SetupText>
						If you can’t find your website’s head section, check your site builder's documentation, or <ContactSupportLink />
					</SetupText>
					<CodeBlock refreshed code={kestrelEmbedCode.getSiteEmbedCode(websitePlatform, user.activationToken)} language="html" />
				</>
			),
		},
		{
			key: "status-page",
			title: "Create Your Default IDX Page",
			canContinue: !!defaultPage,
			content: (
				<>
					<SetupText>
						On your website, create a new page at the URL below. This page displays your IDX content, and doesn't need to appear in your site's navigation.
					</SetupText>
					<SetupCallout>The URL must match exactly, or IDX content won't load.</SetupCallout>
					{defaultPage ? (
						<CopyValue label="Default IDX Page URL" value={pageUrl(baseUrl, defaultPage.path)} />
					) : (
						<Mui.CircularProgress size={24} aria-label="Loading your Default IDX Page" />
					)}
				</>
			),
		},
		{
			key: "body-script",
			title: "Load Your IDX Content",
			content: (
				<>
					<SetupCallout>
						Paste this script into the body of the page you created in the previous step. This loads your IDX content, like listings and search, onto that page.
					</SetupCallout>
					<SetupText>In most platforms, this is done by adding a custom code or embed block to the page content.</SetupText>
					<SetupText>
						Having trouble? Script rendering varies by site builder. For platform-specific help, <ContactSupportLink />
					</SetupText>
					<CodeBlock refreshed code={kestrelEmbedCode.page(websitePlatform)} language="html" />
				</>
			),
		},
		{
			key: "search-page",
			title: "Set Up Your IDX Pages",
			canContinue: !!searchPage,
			content: (
				<>
					<SetupCallout>
						At minimum, we recommend creating your Search page so visitors can find your listings. These pages ensure your listings and search results appear at clean URLs.
					</SetupCallout>
					{searchPage ? (
						<PageSetupInstructions
							pageLabel={PageType.LISTINGS.label}
							url={pageUrl(baseUrl, searchPage.path)}
							websitePlatform={websitePlatform}
						/>
					) : (
						<Mui.CircularProgress size={24} aria-label="Loading your Search page" />
					)}
				</>
			),
		},
	];

	// Other starts at platform and URL, which its later steps build URLs from. Switching the radio on
	// that first step switches the steps after it. Wix installs through the App Market, and WordPress
	// registers from the Optima Express plugin with the key the IDX page shows (Dave's pass), so for
	// both the platform step is the whole wizard (VPLAT 13).
	const steps: WizardStep[] = choice.other ? [platformStep, ...otherSteps] : [platformStep];

	const handleComplete = async () => {
		if (choice.other) {
			await enableSetupPages();
		}
		onClose();
	};

	return (
		<WizardDialog
			open={open}
			title="Set Up IDX"
			steps={steps}
			onClose={onClose}
			onComplete={handleComplete}
		/>
	);
};

export const ConnectIdxWizard = withStyles(Component, styles);
